/**
 * Free gift promo: claim via popup, auto-add when cart subtotal meets threshold.
 * Config is provided by <free-gift-config> (from free-gift-popup section).
 */
(function () {
  const STORAGE_CLAIMED = 'andara:free-headband-claimed';
  const STORAGE_DISMISSED = 'andara:free-headband-dismissed';
  const STORAGE_UNAVAILABLE = 'andara:free-headband-unavailable';

  function cartAddUrl() {
    const base = (window.theme && theme.routes && theme.routes.cart_add_url) || '/cart/add';
    return base.indexOf('.js') === -1 ? `${base}.js` : base;
  }

  function cartUrl() {
    const base = (window.theme && theme.routes && theme.routes.cart_url) || '/cart';
    return base.indexOf('.js') === -1 ? `${base}.js` : base;
  }

  function cartUpdateUrl() {
    const base = (window.theme && theme.routes && theme.routes.cart_update_url) || '/cart/update';
    return base.indexOf('.js') === -1 ? `${base}.js` : base;
  }

  class FreeGiftManager {
    constructor(config) {
      this.config = config;
      this.variantId = String(config.variantId || '');
      this.productId = String(config.productId || '');
      this.threshold = parseInt(config.threshold, 10) || 19900; // cents (legacy)
      this.triggerVariantId = String(config.triggerVariantId || '');
      this.triggerProductId = String(config.triggerProductId || '');
      this.triggerQuantity = parseInt(config.triggerQuantity, 10) || 2;
      // Alternate trigger (OR): e.g. 1× 8-pack also qualifies
      this.triggerVariantId2 = String(config.triggerVariantId2 || '');
      this.triggerProductId2 = String(config.triggerProductId2 || '');
      this.triggerQuantity2 = parseInt(config.triggerQuantity2, 10) || 1;
      this.useTriggerProduct =
        !!this.triggerProductId ||
        !!this.triggerVariantId ||
        !!this.triggerProductId2 ||
        !!this.triggerVariantId2;
      this.busy = false;
      this.pending = false;
      this.failCount = 0;

      if (!this.variantId) {
        console.warn('[free-gift] missing variant id');
        return;
      }

      this.onCartEvent = this.scheduleSync.bind(this);
      document.addEventListener('cart:updated', this.onCartEvent);
      document.addEventListener('ajaxProduct:added', this.onCartEvent);
      document.addEventListener('cartdrawer:opened', this.onCartEvent);
      document.addEventListener('page:loaded', this.onCartEvent);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        try {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartEvent);
        } catch (e) {
          /* ignore */
        }
      }

      // Initial sync
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.scheduleSync());
      } else {
        this.scheduleSync();
      }

      // Periodic safety net while claimed (covers missed events)
      this.interval = window.setInterval(() => {
        if (this.claimed) this.scheduleSync();
      }, 4000);
    }

    cartQtyForTrigger(cart, productId, variantId) {
      return (cart.items || [])
        .filter((item) => {
          if (variantId && String(item.variant_id) === String(variantId)) {
            return true;
          }
          if (productId && String(item.product_id) === String(productId)) {
            return true;
          }
          return false;
        })
        .reduce((sum, item) => sum + item.quantity, 0);
    }

    meetsCondition(cart) {
      if (this.useTriggerProduct) {
        const hasPrimary =
          !!(this.triggerProductId || this.triggerVariantId) &&
          this.cartQtyForTrigger(cart, this.triggerProductId, this.triggerVariantId) >=
            this.triggerQuantity;

        const hasAlternate =
          !!(this.triggerProductId2 || this.triggerVariantId2) &&
          this.cartQtyForTrigger(cart, this.triggerProductId2, this.triggerVariantId2) >=
            this.triggerQuantity2;

        // Qualify via either path (OR)
        if (this.triggerProductId || this.triggerVariantId || this.triggerProductId2 || this.triggerVariantId2) {
          return hasPrimary || hasAlternate;
        }
      }

      // Legacy: subtotal threshold
      const giftSubtotal = (cart.items || [])
        .filter((item) => String(item.variant_id) === this.variantId)
        .reduce((sum, item) => sum + (item.final_line_price || item.line_price || 0), 0);
      const eligibleSubtotal = (cart.items_subtotal_price || cart.total_price || 0) - giftSubtotal;
      return eligibleSubtotal >= this.threshold;
    }

    get claimed() {
      try {
        return window.localStorage.getItem(STORAGE_CLAIMED) === '1';
      } catch (e) {
        return this.getCookie(STORAGE_CLAIMED) === '1';
      }
    }

    set claimed(value) {
      try {
        if (value) {
          window.localStorage.setItem(STORAGE_CLAIMED, '1');
          window.localStorage.removeItem(STORAGE_UNAVAILABLE);
        } else {
          window.localStorage.removeItem(STORAGE_CLAIMED);
        }
      } catch (e) {
        /* ignore */
      }
      if (value) {
        this.setCookie(STORAGE_CLAIMED, 60);
        this.removeCookie(STORAGE_UNAVAILABLE);
      } else {
        this.removeCookie(STORAGE_CLAIMED);
      }
      this.failCount = 0;
    }

    get unavailable() {
      // Only treat as unavailable after several hard failures this session,
      // or an explicit sold-out flag — never permanently block after one fail.
      try {
        if (window.localStorage.getItem(STORAGE_UNAVAILABLE) === '1' && this.failCount >= 2) {
          return true;
        }
      } catch (e) {
        /* ignore */
      }
      return this.failCount >= 5;
    }

    markUnavailable(permanent) {
      this.failCount += 1;
      if (permanent) {
        try {
          window.localStorage.setItem(STORAGE_UNAVAILABLE, '1');
        } catch (e) {
          /* ignore */
        }
      }
    }

    claim(options = {}) {
      this.claimed = true;
      try {
        window.localStorage.setItem(STORAGE_DISMISSED, '1');
        window.localStorage.removeItem(STORAGE_UNAVAILABLE);
      } catch (e) {
        /* ignore */
      }
      this.setCookie(STORAGE_DISMISSED, 60);
      this.removeCookie(STORAGE_UNAVAILABLE);
      this.failCount = 0;
      this.scheduleSync();

      if (options.notify !== false) {
        this.showClaimToast(options.message);
      }
    }

    showClaimToast(message) {
      const text =
        message ||
        "You're in! Add 2 of the 4-Pack or 1 of the 8-Pack and the free headband will be added to your cart.";

      let toast = document.getElementById('andara-free-gift-toast');
      if (!toast) {
        toast = document.createElement('div');
        toast.id = 'andara-free-gift-toast';
        toast.setAttribute('role', 'status');
        toast.style.cssText = [
          'position:fixed',
          'left:50%',
          'bottom:1.25rem',
          'transform:translateX(-50%)',
          'z-index:9999',
          'max-width:min(92vw,28rem)',
          'padding:0.9rem 1.1rem',
          'border-radius:999px',
          'background:#75049b',
          'color:#fff',
          'font-size:0.9rem',
          'line-height:1.35',
          'text-align:center',
          'box-shadow:0 10px 30px rgba(0,0,0,.2)',
          'opacity:0',
          'transition:opacity .25s ease, transform .25s ease',
          'pointer-events:none'
        ].join(';');
        document.body.appendChild(toast);
      }

      toast.textContent = text;
      requestAnimationFrame(() => {
        toast.style.opacity = '1';
        toast.style.transform = 'translateX(-50%) translateY(-4px)';
      });

      window.clearTimeout(this._toastTimer);
      this._toastTimer = window.setTimeout(() => {
        toast.style.opacity = '0';
        toast.style.transform = 'translateX(-50%) translateY(0)';
      }, 3200);
    }

    scheduleSync() {
      if (this.pending) return;
      this.pending = true;
      setTimeout(() => {
        this.pending = false;
        this.sync();
      }, 300);
    }

    async sync() {
      if (this.busy || !this.variantId) return;

      // Nothing to do if not claimed and gift not already in cart
      // (still need to remove unauthorized gift if present)
      this.busy = true;

      try {
        const cart = await this.fetchCart();
        if (!cart) return;

        const giftLines = (cart.items || []).filter((item) => String(item.variant_id) === this.variantId);
        const giftInCart = giftLines.length > 0;
        const meetsThreshold = this.meetsCondition(cart);
        const shouldHaveGift = this.claimed && meetsThreshold;

        if (shouldHaveGift && !giftInCart) {
          if (this.unavailable) {
            console.warn('[free-gift] eligible but marked unavailable; skipping add');
            return;
          }
          console.info('[free-gift] adding gift', {
            variantId: this.variantId,
            meetsThreshold
          });
          await this.addGift();
        } else if (!shouldHaveGift && giftInCart) {
          console.info('[free-gift] removing gift', {
            claimed: this.claimed,
            meetsThreshold
          });
          await this.removeGift();
        } else if (giftInCart) {
          const totalQty = giftLines.reduce((sum, item) => sum + item.quantity, 0);
          if (totalQty !== 1 || giftLines.length > 1) {
            await this.setGiftQuantity(1);
          }
        }
      } catch (error) {
        console.error('[free-gift] sync error', error);
      } finally {
        this.busy = false;
      }
    }

    fetchCart() {
      return fetch(cartUrl(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then((res) => (res.ok ? res.json() : null));
    }

    getSections() {
      const sections = ['mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble', 'cart-live-region-text'];
      if (document.body.classList.contains('template-cart')) {
        // On cart page, section IDs are dynamic; still request common ones
        const cartItems = document.querySelector('#main-cart-items');
        if (cartItems && cartItems.dataset.id) sections.push(cartItems.dataset.id);
        const cartFooter = document.querySelector('#main-cart-footer');
        if (cartFooter && cartFooter.dataset.id) sections.push(cartFooter.dataset.id);
      }
      return sections;
    }

    async addGift() {
      // Match product-form.js style (FormData) for best storefront compatibility
      const formData = new FormData();
      formData.append('id', this.variantId);
      formData.append('quantity', '1');
      formData.append('properties[_free_gift]', 'true');
      formData.append('properties[Offer]', 'Free gift');
      formData.append('sections', this.getSections().join(','));
      formData.append('sections_url', window.location.pathname);

      const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' }
      };
      config.headers = config.headers || {};
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;
      config.credentials = 'same-origin';

      let response;
      let data;
      try {
        response = await fetch(cartAddUrl(), config);
        data = await response.json();
      } catch (error) {
        console.error('[free-gift] add network/parse error', error);
        this.markUnavailable(false);
        return;
      }

      if (data.status && Number(data.status) >= 400) {
        const message = `${data.message || ''} ${data.description || ''} ${JSON.stringify(data.errors || {})}`;
        console.warn('[free-gift] add failed', data);

        // Sold out / inventory: stop for real
        if (/sold out|not enough|inventory|unavailable/i.test(message) || response.status === 422) {
          // 422 can also mean unpublished — only permanent if clearly inventory
          if (/sold out|not enough|inventory/i.test(message)) {
            this.markUnavailable(true);
          } else {
            this.markUnavailable(false);
          }
        } else {
          this.markUnavailable(false);
        }
        return;
      }

      // Success — clear fail state
      this.failCount = 0;
      try {
        window.localStorage.removeItem(STORAGE_UNAVAILABLE);
      } catch (e) {
        /* ignore */
      }

      this.renderCart(data);

      // If sections missing (common on cart page), hard refresh cart page
      if (document.body.classList.contains('template-cart')) {
        window.location.href = (window.theme && theme.routes && theme.routes.cart_url) || '/cart';
      }
    }

    async removeGift() {
      const body = JSON.stringify({
        updates: {
          [this.variantId]: 0
        },
        sections: this.getSections(),
        sections_url: window.location.pathname
      });

      const config = typeof fetchConfig === 'function' ? fetchConfig() : {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
      };
      config.body = body;
      config.credentials = 'same-origin';

      const response = await fetch(cartUpdateUrl(), config);
      const data = await response.json();
      this.renderCart(data);

      if (document.body.classList.contains('template-cart')) {
        window.location.href = (window.theme && theme.routes && theme.routes.cart_url) || '/cart';
      }
    }

    async setGiftQuantity(qty) {
      const body = JSON.stringify({
        updates: {
          [this.variantId]: qty
        },
        sections: this.getSections(),
        sections_url: window.location.pathname
      });

      const config = typeof fetchConfig === 'function' ? fetchConfig() : {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
      };
      config.body = body;
      config.credentials = 'same-origin';

      const response = await fetch(cartUpdateUrl(), config);
      const data = await response.json();
      this.renderCart(data);
    }

    renderCart(response) {
      if (!response) return;

      const miniCart = document.querySelector('mini-cart');
      if (miniCart && response.sections) {
        const sections = miniCart.getSectionsToRender
          ? miniCart.getSectionsToRender()
          : [
              { id: 'mini-cart', section: 'mini-cart', selector: '.shopify-section' },
              { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
              { id: 'mobile-cart-icon-bubble', section: 'mobile-cart-icon-bubble', selector: '.shopify-section' }
            ];

        sections.forEach((section) => {
          const el = document.getElementById(section.id);
          const sectionHtml = response.sections[section.id] || response.sections[section.section];
          if (!el || !sectionHtml) return;
          const html = miniCart.getSectionInnerHTML
            ? miniCart.getSectionInnerHTML(sectionHtml, section.selector)
            : sectionHtml;
          el.innerHTML = html;
        });
      }

      document.dispatchEvent(new CustomEvent('free-gift:synced', { detail: response }));
    }

    getCookie(name) {
      const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
      return match ? match[2] : null;
    }

    setCookie(name, expiryDays) {
      document.cookie = `${name}=1; max-age=${expiryDays * 24 * 60 * 60}; path=/`;
    }

    removeCookie(name) {
      document.cookie = `${name}=; max-age=0; path=/`;
    }
  }

  function readFreeGiftConfigFromEl(el) {
    if (!el) return null;
    // Use getAttribute for reliability. Note: data-*-2 does NOT map to
    // dataset.*2 (HTML maps it to keys ending in "-2"), so path B uses
    // data-trigger2-* which camelCases cleanly to trigger2*.
    const attr = (name) => {
      const value = el.getAttribute(name);
      return value == null ? '' : value;
    };
    return {
      variantId: attr('data-variant-id') || el.dataset.variantId,
      productId: attr('data-product-id') || el.dataset.productId,
      threshold: attr('data-threshold') || el.dataset.threshold,
      triggerVariantId: attr('data-trigger-variant-id') || el.dataset.triggerVariantId,
      triggerProductId: attr('data-trigger-product-id') || el.dataset.triggerProductId,
      triggerQuantity: attr('data-trigger-quantity') || el.dataset.triggerQuantity,
      triggerVariantId2:
        attr('data-trigger2-variant-id') ||
        attr('data-trigger-variant-id-2') ||
        el.dataset.trigger2VariantId,
      triggerProductId2:
        attr('data-trigger2-product-id') ||
        attr('data-trigger-product-id-2') ||
        el.dataset.trigger2ProductId,
      triggerQuantity2:
        attr('data-trigger2-quantity') ||
        attr('data-trigger-quantity-2') ||
        el.dataset.trigger2Quantity,
      enabled: attr('data-enabled') !== 'false'
    };
  }

  class FreeGiftConfig extends HTMLElement {
    connectedCallback() {
      // Prefer section config; replace any incomplete fallback manager
      // (e.g. claim-from-link before this element upgraded).
      const config = readFreeGiftConfigFromEl(this);

      if (!config || !config.enabled || !config.variantId) {
        console.warn('[free-gift] config missing', config);
        return;
      }

      const existing = window.AndaraFreeGift;
      const existingIncomplete =
        existing &&
        !existing.triggerProductId2 &&
        !existing.triggerProductId &&
        !existing.useTriggerProduct;

      if (existing && !existingIncomplete) {
        // Keep the live manager; refresh trigger fields in case settings changed
        existing.triggerVariantId = String(config.triggerVariantId || '');
        existing.triggerProductId = String(config.triggerProductId || '');
        existing.triggerQuantity = parseInt(config.triggerQuantity, 10) || 2;
        existing.triggerVariantId2 = String(config.triggerVariantId2 || '');
        existing.triggerProductId2 = String(config.triggerProductId2 || '');
        existing.triggerQuantity2 = parseInt(config.triggerQuantity2, 10) || 1;
        existing.useTriggerProduct =
          !!existing.triggerProductId ||
          !!existing.triggerVariantId ||
          !!existing.triggerProductId2 ||
          !!existing.triggerVariantId2;
        existing.scheduleSync();
        console.info('[free-gift] config refreshed', {
          triggerProductId: existing.triggerProductId,
          triggerQuantity: existing.triggerQuantity,
          triggerProductId2: existing.triggerProductId2,
          triggerQuantity2: existing.triggerQuantity2,
          claimed: existing.claimed
        });
        return;
      }

      window.AndaraFreeGift = new FreeGiftManager(config);
      console.info('[free-gift] ready', {
        variantId: config.variantId,
        triggerProductId: config.triggerProductId,
        triggerQuantity: config.triggerQuantity,
        triggerProductId2: config.triggerProductId2,
        triggerQuantity2: config.triggerQuantity2,
        claimed: window.AndaraFreeGift.claimed
      });
    }
  }

  class FreeGiftPopup extends HTMLElement {
    constructor() {
      super();

      if (window.location.pathname === '/challenge') return;

      this.cookieName = STORAGE_DISMISSED;
      this.classes = {
        bodyClass: 'promo-popup--open',
        activeClass: 'is-active',
        closingClass: 'is-closing',
        showImage: 'show-image',
        claimedClass: 'is-claimed'
      };

      this.popup = this.querySelector('.free-gift-popup');
      this.claimButton = this.querySelector('[data-free-gift-claim]');
      this.successEl = this.querySelector('[data-free-gift-success]');
    }

    connectedCallback() {
      if (!this.popup) return;

      this.querySelectorAll('[data-free-gift-close]').forEach((button) => {
        button.addEventListener('click', this.onCloseClick.bind(this));
      });

      if (this.claimButton) {
        this.claimButton.addEventListener('click', this.onClaimClick.bind(this));
      }

      if (window.Shopify && Shopify.designMode) {
        this.onShopifySectionSelect = (event) => {
          if (event.target && event.target.contains(this)) this.open();
        };
        this.onShopifySectionDeselect = (event) => {
          if (event.target && event.target.contains(this)) this.close(true);
        };
        document.addEventListener('shopify:section:select', this.onShopifySectionSelect);
        document.addEventListener('shopify:section:deselect', this.onShopifySectionDeselect);
        return;
      }

      if (this.shouldShow()) {
        const delay = (parseInt(this.dataset.delay, 10) || 3) * 1000;
        setTimeout(() => this.open(), delay);
      } else {
        this.emitLifecycle('free-gift:skipped');
      }
    }

    emitLifecycle(name) {
      document.dispatchEvent(
        new CustomEvent(name, {
          bubbles: true,
          detail: { source: 'free-gift-popup' }
        })
      );
    }

    shouldShow() {
      if (this.dataset.testMode === 'true') return true;
      if (this.getCookie(this.cookieName) || this.getLocal(STORAGE_DISMISSED) || this.getLocal(STORAGE_CLAIMED)) {
        return false;
      }
      return true;
    }

    onCloseClick(event) {
      event.preventDefault();
      this.close(false);
    }

    onClaimClick(event) {
      event.preventDefault();

      if (window.AndaraFreeGift) {
        window.AndaraFreeGift.claim();
      } else {
        try {
          window.localStorage.setItem(STORAGE_CLAIMED, '1');
          window.localStorage.setItem(STORAGE_DISMISSED, '1');
          window.localStorage.removeItem(STORAGE_UNAVAILABLE);
        } catch (e) {
          /* ignore */
        }
        this.setCookie(STORAGE_CLAIMED, 60);
        this.setCookie(STORAGE_DISMISSED, 60);
      }

      this.classList.add(this.classes.claimedClass);
      if (this.claimButton) this.claimButton.hidden = true;
      if (this.successEl) this.successEl.hidden = false;

      setTimeout(() => this.close(true), 1600);
    }

    open() {
      this.popup.classList.add(this.classes.activeClass);
      setTimeout(() => {
        this.popup.classList.add(this.classes.showImage);
      }, 400);

      if (this.popup.dataset.position === 'center') {
        if (typeof setScrollbarWidth === 'function') setScrollbarWidth();
        document.body.classList.add(this.classes.bodyClass);
      }
    }

    close(skipCookie) {
      this.popup.classList.add(this.classes.closingClass);

      setTimeout(() => {
        this.popup.classList.remove(this.classes.activeClass);
        this.popup.classList.remove(this.classes.closingClass);
        this.popup.classList.remove(this.classes.showImage);

        if (this.popup.dataset.position === 'center') {
          document.body.classList.remove(this.classes.bodyClass);
        }

        // Fire after the close animation so the next modal does not stack.
        if (!this._closedEmitted) {
          this._closedEmitted = true;
          this.emitLifecycle('free-gift:closed');
        }
      }, 500);

      if (skipCookie || this.dataset.testMode === 'true') return;

      const expiry = parseInt(this.dataset.expiry, 10) || 14;
      this.setCookie(this.cookieName, expiry);
      try {
        window.localStorage.setItem(STORAGE_DISMISSED, '1');
      } catch (e) {
        /* ignore */
      }
    }

    getLocal(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (e) {
        return null;
      }
    }

    getCookie(name) {
      const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
      return match ? match[2] : null;
    }

    setCookie(name, expiryDays) {
      document.cookie = `${name}=1; max-age=${expiryDays * 24 * 60 * 60}; path=/`;
    }
  }

  if (!customElements.get('free-gift-config')) {
    customElements.define('free-gift-config', FreeGiftConfig);
  }
  if (!customElements.get('free-gift-popup')) {
    customElements.define('free-gift-popup', FreeGiftPopup);
  }

  /**
   * Announcement bar / any link can claim the free gift by pointing to:
   *   #claim-free-gift
   * or including ?claim_free_gift=1 / claim-free-gift in the URL.
   */
  function isFreeGiftClaimLink(anchor) {
    if (!anchor || !anchor.getAttribute) return false;
    const href = anchor.getAttribute('href') || '';
    if (!href) return false;

    if (href === '#claim-free-gift' || href.indexOf('#claim-free-gift') !== -1) {
      return true;
    }

    try {
      const url = new URL(href, window.location.origin);
      if (url.searchParams.get('claim_free_gift') === '1') return true;
      if (url.hash === '#claim-free-gift') return true;
      if (url.pathname.indexOf('claim-free-gift') !== -1) return true;
    } catch (e) {
      if (/claim[_-]?free[_-]?gift/i.test(href)) return true;
    }

    return false;
  }

  function claimFromLink(options = {}) {
    if (window.AndaraFreeGift) {
      window.AndaraFreeGift.claim(options);
      return;
    }

    // Bootstrap from <free-gift-config> when present so path B triggers aren't lost
    const configEl = document.querySelector('free-gift-config');
    const config = readFreeGiftConfigFromEl(configEl) || {
      variantId: '54209441333461',
      productId: '9462090629333',
      threshold: 19900
    };

    try {
      window.localStorage.setItem(STORAGE_CLAIMED, '1');
      window.localStorage.setItem(STORAGE_DISMISSED, '1');
      window.localStorage.removeItem(STORAGE_UNAVAILABLE);
    } catch (e) {
      /* ignore */
    }
    document.cookie = `${STORAGE_CLAIMED}=1; max-age=${60 * 24 * 60 * 60}; path=/`;
    document.cookie = `${STORAGE_DISMISSED}=1; max-age=${60 * 24 * 60 * 60}; path=/`;
    document.cookie = `${STORAGE_UNAVAILABLE}=; max-age=0; path=/`;

    const manager = new FreeGiftManager(config);
    window.AndaraFreeGift = manager;
    manager.claim(options);
  }

  function bindFreeGiftClaimLinks() {
    document.addEventListener(
      'click',
      (event) => {
        const anchor = event.target && event.target.closest ? event.target.closest('a') : null;
        if (!anchor || !isFreeGiftClaimLink(anchor)) return;

        event.preventDefault();
        event.stopPropagation();
        claimFromLink({
          message:
            "You're in! Add 2 of the 4-Pack or 1 of the 8-Pack and the free headband will be added to your cart."
        });
      },
      true
    );

    // Support landing on a claim URL directly
    try {
      const params = new URLSearchParams(window.location.search);
      if (
        params.get('claim_free_gift') === '1' ||
        window.location.hash === '#claim-free-gift'
      ) {
        claimFromLink({ notify: true });
      }
    } catch (e) {
      /* ignore */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindFreeGiftClaimLinks);
  } else {
    bindFreeGiftClaimLinks();
  }

  /**
   * Sequence Klaviyo Welcome Popup (XZQ4Dh) after Free Gift.
   * - Free Gift still opens on its own delay (default 3s).
   * - If Free Gift will show: hold Klaviyo, then open it immediately when Free Gift closes.
   * - If Free Gift was already dismissed/claimed (or missing): leave Klaviyo's normal delay alone.
   * - If Klaviyo tries to open while Free Gift is up, suppress and open after close instead.
   */
  (function setupKlaviyoAfterFreeGift() {
    const KLAVIYO_POPUP_FORM_ID = 'XZQ4Dh';
    const HOLD_CLASS = 'andara-hold-klaviyo';
    const HELD_ATTR = 'data-andara-klaviyo-held';

    let blocking = false;
    let resolved = false;
    let suppressedWhileBlocking = false;
    let reopened = false;

    function getLocal(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (e) {
        return null;
      }
    }

    function getCookie(name) {
      const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
      return match ? match[2] : null;
    }

    function freeGiftWillShow() {
      if (window.Shopify && Shopify.designMode) return false;

      const el = document.querySelector('free-gift-popup');
      if (!el) return false;

      // Mirror FreeGiftPopup.shouldShow()
      if (el.dataset.testMode === 'true') return true;
      if (
        getCookie(STORAGE_DISMISSED) ||
        getLocal(STORAGE_DISMISSED) ||
        getLocal(STORAGE_CLAIMED)
      ) {
        return false;
      }
      return true;
    }

    function ensureHoldStyles() {
      if (document.getElementById('andara-klaviyo-hold-style')) return;
      const style = document.createElement('style');
      style.id = 'andara-klaviyo-hold-style';
      // Hide only the popup form while Free Gift is first in line.
      // Embeds (e.g. klaviyo-form-R6Y6B2) stay visible in page content.
      style.textContent = `
        html.${HOLD_CLASS} [data-testid="klaviyo-form-${KLAVIYO_POPUP_FORM_ID}"],
        html.${HOLD_CLASS} [data-testid="klaviyo-form-${KLAVIYO_POPUP_FORM_ID}"] *,
        html.${HOLD_CLASS} .klaviyo-form-${KLAVIYO_POPUP_FORM_ID}[role="dialog"],
        html.${HOLD_CLASS} [${HELD_ATTR}="1"] {
          opacity: 0 !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }
      `;
      document.head.appendChild(style);
    }

    function holdKlaviyoNode(node) {
      if (!node || node.nodeType !== 1) return;
      if (node.getAttribute(HELD_ATTR) === '1') return;

      // Never hide in-page embeds (hero / email signup).
      if (node.closest && node.closest('.klaviyo-form-R6Y6B2, .klaviyo-form-WkGAXU, .email-signup-klaviyo, .ai-hero-email-signup__form-wrapper-as2swdflrveldoefsnaigenblock0029b1ctjhtj7')) {
        return;
      }

      const testId = node.getAttribute && node.getAttribute('data-testid');
      const isTargetForm =
        testId === `klaviyo-form-${KLAVIYO_POPUP_FORM_ID}` ||
        (node.classList && node.classList.contains(`klaviyo-form-${KLAVIYO_POPUP_FORM_ID}`)) ||
        (node.id && String(node.id).indexOf(KLAVIYO_POPUP_FORM_ID) !== -1) ||
        (typeof node.innerHTML === 'string' &&
          node.childElementCount > 0 &&
          node.querySelector &&
          node.querySelector(`[data-testid="klaviyo-form-${KLAVIYO_POPUP_FORM_ID}"]`));

      // Body-level Klaviyo flyout shells often lack the form id on the root.
      const isLikelyPopupShell =
        node.parentElement === document.body &&
        ((node.className && String(node.className).indexOf('kl-private-reset') !== -1) ||
          (node.className && String(node.className).indexOf('needsclick') !== -1)) &&
        !node.querySelector('.klaviyo-form-R6Y6B2, .klaviyo-form-WkGAXU');

      if (!isTargetForm && !isLikelyPopupShell) return;

      const target = isTargetForm && node.querySelector
        ? node.closest('body > div') || node
        : node;

      target.setAttribute(HELD_ATTR, '1');
      target.style.setProperty('opacity', '0', 'important');
      target.style.setProperty('visibility', 'hidden', 'important');
      target.style.setProperty('pointer-events', 'none', 'important');
    }

    function releaseHeldNodes() {
      document.querySelectorAll(`[${HELD_ATTR}="1"]`).forEach((node) => {
        node.removeAttribute(HELD_ATTR);
        node.style.removeProperty('opacity');
        node.style.removeProperty('visibility');
        node.style.removeProperty('pointer-events');
      });
    }

    function klaviyoPush(command, formId) {
      try {
        if (window.klaviyo && typeof window.klaviyo.push === 'function') {
          window.klaviyo.push([command, formId]);
          return;
        }
      } catch (e) {
        /* fall through */
      }
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push([command, formId]);
    }

    function openKlaviyoPopup() {
      if (reopened) return;
      reopened = true;
      klaviyoPush('openForm', KLAVIYO_POPUP_FORM_ID);
    }

    function resolve(options) {
      if (resolved) return;
      resolved = true;
      blocking = false;
      document.documentElement.classList.remove(HOLD_CLASS);
      releaseHeldNodes();

      // After Free Gift was shown and closed: open Klaviyo immediately
      // (do not wait out the remaining 25s delay). Skip force-open when Free Gift
      // never showed (already dismissed) so Klaviyo keeps its normal timing.
      const shouldForceOpen =
        options && Object.prototype.hasOwnProperty.call(options, 'forceOpen')
          ? !!options.forceOpen
          : suppressedWhileBlocking;
      if (shouldForceOpen) {
        // Brief beat after Free Gift finishes closing.
        window.setTimeout(openKlaviyoPopup, (options && options.delay) || 400);
      }
    }

    function onKlaviyoForms(event) {
      if (!blocking || resolved) return;
      const detail = event && event.detail ? event.detail : {};
      if (detail.type !== 'open') return;
      if (detail.formId && detail.formId !== KLAVIYO_POPUP_FORM_ID) return;

      suppressedWhileBlocking = true;

      // Hide any injected popup UI, then close so it is not left open under Free Gift.
      window.requestAnimationFrame(() => {
        document
          .querySelectorAll(
            `[data-testid="klaviyo-form-${KLAVIYO_POPUP_FORM_ID}"], .klaviyo-form-${KLAVIYO_POPUP_FORM_ID}, body > div.needsclick, body > div[class*="kl-private-reset"]`
          )
          .forEach(holdKlaviyoNode);
      });

      // closeForm can set Klaviyo's closed cookie; we re-open with openForm after Free Gift.
      klaviyoPush('closeForm', KLAVIYO_POPUP_FORM_ID);
    }

    function startObserver() {
      if (!window.MutationObserver || !document.body) return null;
      const observer = new MutationObserver((mutations) => {
        if (!blocking || resolved) return;
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType !== 1) return;
            holdKlaviyoNode(node);
            if (node.querySelectorAll) {
              node
                .querySelectorAll(
                  `[data-testid="klaviyo-form-${KLAVIYO_POPUP_FORM_ID}"], [class*="kl-private-reset"], .needsclick`
                )
                .forEach(holdKlaviyoNode);
            }
          });
        });
      });
      observer.observe(document.body, { childList: true, subtree: true });
      return observer;
    }

    function init() {
      if (!freeGiftWillShow()) {
        resolved = true;
        blocking = false;
        return;
      }

      blocking = true;
      ensureHoldStyles();
      document.documentElement.classList.add(HOLD_CLASS);
      startObserver();

      // Safety: if Free Gift never opens (section removed mid-session), do not hold forever.
      window.setTimeout(() => {
        if (resolved) return;
        const active = document.querySelector('.free-gift-popup.is-active');
        if (!active) {
          resolve({ forceOpen: false });
        }
      }, 15000);
    }

    // Free Gift was shown and closed/claimed → open Klaviyo right away.
    document.addEventListener('free-gift:closed', () => resolve({ forceOpen: true }));
    // Free Gift never showed this visit → leave Klaviyo's normal 25s delay alone.
    document.addEventListener('free-gift:skipped', () => resolve({ forceOpen: false }));
    window.addEventListener('klaviyoForms', onKlaviyoForms);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  })();
})();
